import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTRPC } from 'src/utils/trpc';

export type UpdateFormFieldPositionsInput = {
  ParentType: ParentType;
  Ordering: { FieldId: string; Position: number }[];
};

/**
 * Raw tRPC implementation of the ordering-only form save (drag-reorder +
 * custom-field add/remove from the inline form's edit mode). NOT imported
 * outside this directory — consumers use the flag-gated
 * `useUpdateFormFieldPositions` wrapper.
 */
export const useUpdateFormFieldPositionsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.formConfiguration.updateFieldOrdering.mutationOptions({
      onSuccess: async () => {
        // Mirrors the legacy evictField(form_configuration) +
        // evictField(form_field_ordering). `getFormCustomisation` backs the
        // live form render (skipping it leaves an open form stale, RSP-5866);
        // `getByParentTypes` backs the widget / category readers.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.formConfiguration.getByParentTypes.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.formConfiguration.getFormCustomisation.queryKey(),
          }),
        ]);
      },
    }),
  });

  // mutateAsync is a stable reference across renders; the surrounding
  // `mutation` object is not. Memoise on it so the returned callback stays
  // referentially stable — consumers thread it through `useCallback`/effect
  // deps (`CustomisableFieldWrapper.saveFields` → `setOnSave`), and an
  // unstable function there drives an infinite render loop (React #185).
  const { mutateAsync } = mutation;
  const updateFormFieldPositions = useCallback(
    async (variables: UpdateFormFieldPositionsInput): Promise<void> => {
      await mutateAsync(variables);
    },
    [mutateAsync]
  );

  return {
    updateFormFieldPositions,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
